import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import SignaturePad from "@/components/SignaturePad";
import { ArrowLeft, MapPin, User, Phone, Boxes, CheckCircle2, Truck, Package,
  Camera, X, Navigation, ChevronRight, WifiOff, MessageSquare, CalendarClock, Edit3, Warehouse,
} from "lucide-react";
import { enqueue, blobToDataUrl } from "@/lib/offlineQueue";
import { formatDate, formatDateTime, packagingLabel } from "@/lib/utils";
import { toast } from "sonner";

// Contextual next-action mapping for the driver flow.
const NEXT_ACTION = {
  geplant:   { status: "abgeholt",   label: "Sendung abgeholt",       Icon: Package },
  erstellt:  { status: "abgeholt",   label: "Sendung abgeholt",       Icon: Package },
  abgeholt:  { status: "unterwegs",  label: "Losfahren / Unterwegs",  Icon: Truck },
  zwischenlager: { status: "unterwegs", label: "Losfahren / Zustellung", Icon: Truck },
  unterwegs: { status: "zugestellt", label: "Zustellung erfassen",    Icon: CheckCircle2 },
};

export default function DriverOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [events, setEvents] = useState([]);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [proofOpen, setProofOpen] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [signatureBlob, setSignatureBlob] = useState(null);
  const fileInputRef = useRef(null);
  // Termin-Editor: vereinbarter Liefer-Termin (datetime-local + Notiz)
  const [apptOpen, setApptOpen] = useState(false);
  const [apptDraft, setApptDraft] = useState({ at: "", note: "" });
  const [savingAppt, setSavingAppt] = useState(false);

  // Derived preview URL — auto-cleans up via effect.
  const photoPreview = useMemo(
    () => (photoFile ? URL.createObjectURL(photoFile) : null),
    [photoFile],
  );
  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  const load = async () => {
    try {
      const [o, t] = await Promise.all([
        api.get(`/orders/${id}`),
        api.get(`/orders/${id}/tracking`),
      ]);
      setOrder(o.data);
      setEvents(t.data);
    } catch (e) { setError(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const openProofModal = () => setProofOpen(true);

  // Helpers for the appointment editor (HTML datetime-local format: "YYYY-MM-DDTHH:mm")
  const fmtForInput = (iso) => (iso ? String(iso).slice(0, 16) : "");
  const fmtForDisplay = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("de-CH", {
        weekday: "short", day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };
  const openApptEditor = () => {
    setApptDraft({
      at: fmtForInput(order?.scheduled_appointment_at),
      note: order?.scheduled_appointment_note || "",
    });
    setApptOpen(true);
  };
  const saveAppointment = async () => {
    setSavingAppt(true);
    try {
      await api.post(`/orders/${id}/appointment`, {
        scheduled_appointment_at: apptDraft.at || null,
        scheduled_appointment_note: apptDraft.note || null,
      });
      toast.success(apptDraft.at ? "Termin gespeichert" : "Termin entfernt");
      setApptOpen(false);
      await load();
    } catch (e) {
      toast.error("Speichern fehlgeschlagen", { description: formatApiError(e) });
    } finally { setSavingAppt(false); }
  };
  const clearAppointment = async () => {
    if (!window.confirm("Termin wirklich entfernen?")) return;
    setApptDraft({ at: "", note: "" });
    setSavingAppt(true);
    try {
      await api.post(`/orders/${id}/appointment`, {
        scheduled_appointment_at: null,
        scheduled_appointment_note: null,
      });
      toast.success("Termin entfernt");
      setApptOpen(false);
      await load();
    } catch (e) {
      toast.error("Entfernen fehlgeschlagen", { description: formatApiError(e) });
    } finally { setSavingAppt(false); }
  };

  const updateStatus = async (status, warehouseLocation = null) => {
    if (status === "zugestellt") {
      openProofModal();
      return;
    }
    const payload = { status, ...(warehouseLocation ? { warehouse_location: warehouseLocation } : {}) };
    setWorking(true);
    setError("");
    try {
      if (!navigator.onLine) {
        enqueue({ type: "status", orderId: id, payload });
        // Optimistically reflect new status in UI even though offline
        setOrder((o) => o ? { ...o, status, ...(warehouseLocation ? { warehouse_location: warehouseLocation } : {}) } : o);
      } else {
        await api.post(`/orders/${id}/status`, payload);
        await load();
      }
    } catch (e) {
      // Network error → enqueue as offline action
      enqueue({ type: "status", orderId: id, payload });
      setOrder((o) => o ? { ...o, status, ...(warehouseLocation ? { warehouse_location: warehouseLocation } : {}) } : o);
    }
    finally { setWorking(false); }
  };

  const submitProof = async () => {
    setWorking(true);
    setError("");
    try {
      if (!photoFile && !signatureBlob) {
        setError("Bitte mindestens Foto oder Unterschrift erfassen.");
        setWorking(false);
        return;
      }

      const tryOnline = navigator.onLine;
      if (tryOnline) {
        try {
          const fd = new FormData();
          if (photoFile) fd.append("photo", photoFile);
          if (signatureBlob) fd.append("signature", signatureBlob, "signature.png");
          await api.post(`/orders/${id}/delivery-proof`, fd);
          await api.post(`/orders/${id}/status`, { status: "zugestellt", notes: "Zugestellt mit Foto/Unterschrift" });
        } catch (e) {
          // Fall through to offline-queue
          const photoDataUrl = photoFile ? await blobToDataUrl(photoFile) : null;
          const signatureDataUrl = signatureBlob ? await blobToDataUrl(signatureBlob) : null;
          enqueue({ type: "proof", orderId: id, payload: { photoDataUrl, signatureDataUrl, thenStatus: "zugestellt" } });
        }
      } else {
        const photoDataUrl = photoFile ? await blobToDataUrl(photoFile) : null;
        const signatureDataUrl = signatureBlob ? await blobToDataUrl(signatureBlob) : null;
        enqueue({ type: "proof", orderId: id, payload: { photoDataUrl, signatureDataUrl, thenStatus: "zugestellt" } });
        setOrder((o) => o ? { ...o, status: "zugestellt" } : o);
      }

      setProofOpen(false);
      setPhotoFile(null);
      setSignatureBlob(null);
      if (navigator.onLine) await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setWorking(false); }
  };

  if (!order) {
    return <Layout><div className="px-6 py-8 text-[var(--text-3)]">{error || "Laden…"}</div></Layout>;
  }

  const next = NEXT_ACTION[order.status];
  const navHref = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    [order.delivery?.address, order.delivery?.zip, order.delivery?.city].filter(Boolean).join(", "),
  )}`;
  const isDone = order.status === "zugestellt" || order.status === "storniert";

  return (
    <Layout>
      <div className="px-4 md:px-10 py-4 md:py-8 max-w-3xl mx-auto pb-44 fade-in">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-[var(--text-2)] hover:text-[var(--text)]">
          <ArrowLeft size={14} strokeWidth={1.5} /> Zurück
        </button>

        {/* Compact header */}
        <div className="mt-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="label-eyebrow text-[10px]" style={{ color: "#D4AF37" }}>Auftrag</div>
              <h1 className="text-2xl font-mono font-semibold tracking-tight mt-0.5 truncate">{order.order_number}</h1>
            </div>
            <StatusBadge status={order.status} />
          </div>
        </div>

        {error && <div className="mt-3 border border-red-500/30 bg-red-500/5 text-red-700 text-sm px-3 py-2 rounded-sm">{error}</div>}

        {/* Pickup card */}
        {order.pickup && (
          <div className="card p-4 mt-4" data-testid="driver-pickup-card">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
              <div className="label-eyebrow text-[10px] text-emerald-700">Abholung</div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin size={18} strokeWidth={1.6} className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-base text-[var(--text)] font-medium leading-tight">{order.pickup?.name}</div>
                <div className="text-sm text-[var(--text)] mt-1">{order.pickup?.address}</div>
                <div className="text-sm text-[var(--text)]">{order.pickup?.zip} {order.pickup?.city}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                  [order.pickup?.address, order.pickup?.zip, order.pickup?.city].filter(Boolean).join(", "),
                )}`}
                target="_blank" rel="noopener noreferrer"
                data-testid="driver-pickup-navi"
                className="flex items-center justify-center gap-2 text-xs font-medium border border-emerald-500/50 text-emerald-700 hover:bg-emerald-500/10 py-2 rounded-sm uppercase tracking-wider"
              >
                <Navigation size={13} strokeWidth={2} /> Navi Abholung
              </a>
              {order.contact_phone && (
                <a
                  href={`tel:${order.contact_phone}`}
                  data-testid="driver-pickup-phone"
                  className="flex items-center justify-center gap-2 text-xs font-medium border border-[var(--border)] text-[var(--text-2)] py-2 rounded-sm uppercase tracking-wider"
                >
                  <Phone size={13} strokeWidth={2} /> {order.contact_phone}
                </a>
              )}
            </div>
          </div>
        )}

        {/* Hero card: address + 2 big quick actions (Navi + Phone) */}
        <div className="card p-4 mt-4">
          <div className="flex items-center gap-2 mb-2">
            <MapPin size={12} strokeWidth={1.6} className="text-[#D4AF37]" />
            <div className="label-eyebrow text-[10px]" style={{ color: "#D4AF37" }}>Lieferung</div>
          </div>
          <div className="flex items-start gap-3">
            <MapPin size={20} strokeWidth={1.6} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-base text-[var(--text)] font-medium leading-tight">{order.delivery?.name}</div>
              <div className="text-sm text-[var(--text)] mt-1">{order.delivery?.address}</div>
              <div className="text-sm text-[var(--text)]">{order.delivery?.zip} {order.delivery?.city}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mt-4">
            <a
              href={navHref}
              target="_blank" rel="noopener noreferrer"
              data-testid="driver-navigate-button"
              className="flex items-center justify-center gap-2 text-sm font-medium bg-[#D4AF37] text-black hover:bg-[#C59B27] active:bg-[#B58D17] py-3 rounded-sm uppercase tracking-wider"
            >
              <Navigation size={16} strokeWidth={2} /> Navi
            </a>
            {order.contact_phone ? (
              <a
                href={`tel:${order.contact_phone}`}
                data-testid="driver-call-button"
                className="flex items-center justify-center gap-2 text-sm border border-[var(--border-strong)] hover:border-[#D4AF37] hover:text-[#D4AF37] py-3 rounded-sm uppercase tracking-wider"
              >
                <Phone size={16} strokeWidth={1.8} /> Anrufen
              </a>
            ) : (
              <div className="flex items-center justify-center text-xs text-[var(--text-3)] border border-[var(--border)] py-3 rounded-sm">
                Kein Telefon
              </div>
            )}
          </div>
        </div>

        {/* Compact info strip */}
        <div className="card p-3 mt-2 grid grid-cols-3 divide-x divide-white/10">
          <div className="px-2 text-center">
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-3)]">Paletten</div>
            <div className="text-base font-semibold text-[var(--text)] mt-0.5">{order.pallet_count}</div>
          </div>
          <div className="px-2 text-center">
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-3)]">Gewicht</div>
            <div className="text-base font-semibold text-[var(--text)] mt-0.5">{order.weight_kg} kg</div>
          </div>
          <div className="px-2 text-center">
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-3)]">{order.delivery_time ? "Zeitfenster" : "Datum"}</div>
            <div className="text-sm font-semibold text-[var(--text)] mt-0.5 truncate">{order.delivery_time || formatDate(order.delivery_date)}</div>
          </div>
        </div>

        {/* Palettentypen / Inhalt — wichtig für die Tourenplanung */}
        {order.positions?.length > 0 && (
          <div className="card p-3 mt-2" data-testid="driver-positions-card">
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-3)] mb-1.5">Inhalt / Palettentyp</div>
            <div className="flex flex-wrap gap-1.5">
              {order.positions.map((p, i) => (
                <span
                  key={i}
                  data-testid={`driver-pos-chip-${i}`}
                  className="px-2 py-1 text-xs font-medium border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[var(--text)] rounded-sm"
                >
                  {p.quantity}× {packagingLabel(p.packaging_type)}{p.is_half_pallet ? " (½)" : ""}{p.weight_kg ? ` · ${p.weight_kg} kg` : ""}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Vereinbarter Liefertermin (vom Fahrer eintragbar) */}
        {!isDone && (
          <div className="card p-4 mt-2" data-testid="driver-appointment-card">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2.5 min-w-0 flex-1">
                <CalendarClock size={18} strokeWidth={1.6} className="text-[#D4AF37] mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="label-eyebrow text-[10px]" style={{ color: "#D4AF37" }}>Termin vereinbart</div>
                  {order.scheduled_appointment_at ? (
                    <>
                      <div className="text-base text-[var(--text)] font-semibold leading-tight mt-0.5" data-testid="driver-appointment-value">
                        {fmtForDisplay(order.scheduled_appointment_at)}
                      </div>
                      {order.scheduled_appointment_note && (
                        <div className="text-xs text-[var(--text-2)] mt-1 whitespace-pre-wrap">{order.scheduled_appointment_note}</div>
                      )}
                    </>
                  ) : (
                    <div className="text-sm text-[var(--text-3)] mt-0.5">Noch kein Termin eingetragen.</div>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={openApptEditor}
                data-testid="driver-appointment-edit"
                className="shrink-0 text-[10px] uppercase tracking-wider px-2.5 py-1.5 border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm flex items-center gap-1"
              >
                <Edit3 size={11} strokeWidth={2} /> {order.scheduled_appointment_at ? "Ändern" : "Eintragen"}
              </button>
            </div>
          </div>
        )}

        {/* Zwischenlager (nur für Fahrer mit hinterlegten Lager-Standorten, z.B. SKY) */}
        {!isDone && order.warehouse_options?.length > 0 && (
          <div className="card p-4 mt-2" data-testid="driver-warehouse-card">
            <div className="flex items-center gap-2 mb-1">
              <Warehouse size={16} strokeWidth={1.6} className="text-orange-700" />
              <div className="label-eyebrow text-[10px] text-orange-700">Zwischenlager</div>
            </div>
            {order.status === "zwischenlager" ? (
              <div className="mt-1" data-testid="driver-warehouse-current">
                <div className="text-base font-semibold text-[var(--text)]">{order.warehouse_location}</div>
                {(() => {
                  const w = order.warehouse_options.find((x) => x.name === order.warehouse_location);
                  return w ? <div className="text-sm text-[var(--text-2)] mt-0.5">{w.address}, {w.zip} {w.city}</div> : null;
                })()}
                <div className="text-xs text-[var(--text-3)] mt-1.5">Sendung eingelagert. Mit „Losfahren / Zustellung" geht es weiter.</div>
              </div>
            ) : ["abgeholt", "unterwegs"].includes(order.status) ? (
              <>
                <p className="text-xs text-[var(--text-2)] mb-2.5">Sendung optional in einem Lager zwischenlagern — oder direkt zustellen:</p>
                <div className="space-y-2">
                  {order.warehouse_options.map((w) => (
                    <button
                      key={w.name}
                      type="button"
                      disabled={working}
                      onClick={() => updateStatus("zwischenlager", w.name)}
                      data-testid={`warehouse-option-${w.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className="w-full flex items-center justify-between gap-3 border border-orange-300/60 hover:bg-orange-50 active:bg-orange-100 rounded-sm px-3 py-2.5 text-left disabled:opacity-50 transition-colors"
                    >
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-[var(--text)]">{w.name}</span>
                        <span className="block text-xs text-[var(--text-2)]">{w.address}, {w.zip} {w.city}</span>
                      </span>
                      <Warehouse size={16} strokeWidth={1.6} className="text-orange-700 shrink-0" />
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-xs text-[var(--text-3)]">Zwischenlagern ist möglich, sobald die Sendung abgeholt wurde.</p>
            )}
          </div>
        )}

        {/* Bemerkungen (vom Kunden / Admin gesetzt) */}
        {order.notes && (
          <div className="card p-4 mt-2" data-testid="driver-notes-card">
            <div className="flex items-start gap-2.5">
              <MessageSquare size={16} strokeWidth={1.6} className="text-[#D4AF37] mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="label-eyebrow text-[10px]" style={{ color: "#D4AF37" }}>Bemerkungen</div>
                <div className="text-sm text-[var(--text)] mt-1 whitespace-pre-wrap leading-snug">{order.notes}</div>
              </div>
            </div>
          </div>
        )}

        {/* Kontakt (compact) */}
        {(order.contact_person || order.customer_order_number) && (
          <div className="card p-3 mt-2 text-sm">
            {order.contact_person && (
              <div className="flex items-center gap-2 text-[var(--text)]">
                <User size={14} className="text-[var(--text-3)]" /> {order.contact_person}
              </div>
            )}
            {order.customer_order_number && (
              <div className="flex items-center gap-2 text-[var(--text-2)] text-xs mt-1">
                <span className="uppercase tracking-wider text-[var(--text-3)]">Best.-Nr.:</span>
                <span className="font-mono">{order.customer_order_number}</span>
              </div>
            )}
          </div>
        )}

        {/* Paletten — collapsed by default for compactness */}
        <details className="card mt-2 px-4 py-3">
          <summary className="cursor-pointer flex items-center justify-between text-sm text-[var(--text)]">
            <span className="flex items-center gap-2"><Boxes size={15} className="text-[#D4AF37]" /> Paletten ({order.pallet_count})</span>
            <ChevronRight size={16} className="text-[var(--text-3)] transition-transform" />
          </summary>
          <div className="mt-3 space-y-1 max-h-72 overflow-auto">
            {order.pallets?.map((p) => (
              <div key={p.id} className="flex items-center justify-between border-b border-[var(--border)] py-1.5 text-sm">
                <span className="text-[var(--text-2)]">{p.index}/{p.total}</span>
                <span className="font-mono tracking-widest text-[var(--text)] text-xs">{p.barcode}</span>
              </div>
            ))}
          </div>
        </details>

        {/* Verlauf — collapsed */}
        <details className="card mt-2 px-4 py-3">
          <summary className="cursor-pointer flex items-center justify-between text-sm text-[var(--text)]">
            <span>Verlauf ({events.length})</span>
            <ChevronRight size={16} className="text-[var(--text-3)] transition-transform" />
          </summary>
          <div className="mt-3 space-y-2.5">
            {events.length === 0 && <div className="text-[var(--text-3)] text-xs">Noch keine Ereignisse.</div>}
            {events.map((ev) => (
              <div key={ev.id} className="border-l-2 border-[#D4AF37]/40 pl-3 py-0.5">
                <StatusBadge status={ev.status} />
                <div className="text-[11px] text-[var(--text-3)] mt-0.5">{formatDateTime(ev.created_at)}</div>
                {ev.notes && <div className="text-xs text-[var(--text-2)] mt-0.5">{ev.notes}</div>}
              </div>
            ))}
          </div>
        </details>

        {isDone && (order.delivery_photo_path || order.signature_path) && (
          <div className="mt-3 text-xs text-emerald-800 border border-emerald-500/30 bg-emerald-500/5 rounded-sm px-3 py-2 flex items-center gap-2">
            <CheckCircle2 size={14} /> Lieferbeleg erfasst — Auftrag abgeschlossen.
          </div>
        )}
      </div>

      {/* STICKY BOTTOM CTA — contextual next action for the driver
          Positioned ABOVE the mobile bottom-nav (lg:hidden) to stay visible.
          --bottom-nav-h is provided by Layout.jsx and collapses for sub-drivers.
          On desktop (lg+), constrain to the right of the 256px sidebar so the
          white gradient does not cover the sidebar's "Abmelden" button. */}
      {next && !isDone && (
        <div
          className="fixed inset-x-0 lg:left-64 z-50 pt-6 px-4 md:px-10"
          style={{
            bottom: "calc(env(safe-area-inset-bottom, 0px) + var(--bottom-nav-h, 0px) + 8px)",
            background: "linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0.95) 60%, rgba(255,255,255,0) 100%)",
            paddingBottom: "0.75rem",
          }}
        >
          <div className="max-w-3xl mx-auto">
            {typeof navigator !== "undefined" && !navigator.onLine && (
              <div className="text-[11px] text-amber-700 mb-2 flex items-center gap-1.5 justify-center">
                <WifiOff size={12} /> Offline — Aktion wird lokal gespeichert und automatisch gesendet.
              </div>
            )}
            <button
              onClick={() => updateStatus(next.status)}
              disabled={working}
              data-testid={`driver-cta-${next.status}`}
              className="w-full flex items-center justify-center gap-3 bg-[#D4AF37] text-black font-semibold py-4 rounded-sm uppercase tracking-wider text-sm active:bg-[#B58D17] hover:bg-[#C59B27] disabled:opacity-50 shadow-2xl shadow-[var(--header)]/20"
            >
              <next.Icon size={20} strokeWidth={2.2} /> {next.label}
            </button>
          </div>
        </div>
      )}

      {/* Proof modal */}
      {proofOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-0 md:p-4 fade-in">
          <div className="card w-full max-w-2xl p-5 md:rounded-sm overflow-y-auto" style={{ maxHeight: "94vh" }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-eyebrow text-[10px]" style={{ color: "#D4AF37" }}>Zustellung</div>
                <h2 className="text-xl font-semibold mt-1">Lieferbeleg erfassen</h2>
              </div>
              <button onClick={() => setProofOpen(false)} className="text-[var(--text-3)] hover:text-[var(--text)] p-2"><X size={20} /></button>
            </div>
            <p className="text-xs text-[var(--text-2)]">
              Bitte erfassen Sie ein Foto und/oder lassen Sie den Empfänger unterschreiben.
            </p>

            <div className="mt-5">
              <div className="label-eyebrow mb-2" style={{ color: "#D4AF37" }}>Lieferfoto</div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                data-testid="proof-photo-input"
              />
              {!photoPreview ? (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="proof-photo-button"
                  className="w-full flex flex-col items-center justify-center border-2 border-dashed border-[var(--border-strong)] hover:border-[#D4AF37]/50 rounded-sm py-8 text-[var(--text-2)] hover:text-[#D4AF37] transition-colors"
                >
                  <Camera size={26} strokeWidth={1.5} />
                  <span className="mt-2 text-sm uppercase tracking-wider">Foto aufnehmen</span>
                  <span className="text-[10px] text-[var(--text-3)] mt-0.5">Tippen zum Öffnen der Kamera</span>
                </button>
              ) : (
                <div className="relative">
                  <img src={photoPreview} alt="Vorschau" className="w-full rounded-sm border border-[var(--border)]" />
                  <button onClick={() => setPhotoFile(null)} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5"><X size={16} /></button>
                </div>
              )}
            </div>

            <div className="mt-5">
              <div className="label-eyebrow mb-2" style={{ color: "#D4AF37" }}>Unterschrift Empfänger</div>
              <SignaturePad onChange={setSignatureBlob} />
            </div>

            <div className="flex gap-2 mt-5 sticky bottom-0 bg-white pt-3 pb-1 border-t border-[var(--border)]">
              <button onClick={() => setProofOpen(false)} className="btn-ghost flex-1 py-3 rounded-sm text-sm uppercase tracking-wider">Abbrechen</button>
              <button
                onClick={submitProof}
                disabled={working}
                data-testid="proof-submit-button"
                className="btn-gold flex-1 py-3 rounded-sm text-sm uppercase tracking-wider"
              >
                {working ? "Speichert…" : "Bestätigen"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Termin-Editor Modal */}
      {apptOpen && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-end md:items-center justify-center p-0 md:p-4 fade-in">
          <div className="card w-full max-w-md p-5 md:rounded-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-eyebrow text-[10px]" style={{ color: "#D4AF37" }}>Liefertermin</div>
                <h2 className="text-xl font-semibold mt-1">Termin vereinbaren</h2>
              </div>
              <button onClick={() => setApptOpen(false)} className="text-[var(--text-3)] hover:text-[var(--text)] p-2" data-testid="appt-close"><X size={20} /></button>
            </div>
            <p className="text-xs text-[var(--text-2)]">
              Datum und Uhrzeit der mit dem Empfänger fix abgemachten Anlieferung. Wird Admin & Kunde sofort sichtbar.
            </p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="label-eyebrow block mb-1.5" style={{ color: "#D4AF37" }}>Datum & Uhrzeit</label>
                <input
                  type="datetime-local"
                  className="input-base"
                  value={apptDraft.at}
                  onChange={(e) => setApptDraft((d) => ({ ...d, at: e.target.value }))}
                  data-testid="appt-datetime-input"
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5" style={{ color: "#D4AF37" }}>Notiz (optional)</label>
                <textarea
                  className="input-base"
                  rows={2}
                  placeholder="z.B. 'Empfänger ruft an wenn er da ist', 'beim Hintereingang anliefern' …"
                  value={apptDraft.note}
                  onChange={(e) => setApptDraft((d) => ({ ...d, note: e.target.value }))}
                  data-testid="appt-note-input"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              {order.scheduled_appointment_at ? (
                <button
                  type="button"
                  onClick={clearAppointment}
                  disabled={savingAppt}
                  data-testid="appt-clear"
                  className="btn-ghost flex-1 py-2.5 rounded-sm text-xs uppercase tracking-wider text-red-700 hover:text-red-800 hover:border-red-500"
                >
                  Entfernen
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setApptOpen(false)}
                  className="btn-ghost flex-1 py-2.5 rounded-sm text-xs uppercase tracking-wider"
                >
                  Abbrechen
                </button>
              )}
              <button
                onClick={saveAppointment}
                disabled={savingAppt || !apptDraft.at}
                data-testid="appt-save"
                className="btn-gold flex-1 py-2.5 rounded-sm text-xs uppercase tracking-wider"
              >
                {savingAppt ? "Speichert…" : "Speichern"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
